// DataHub Mock Data - Based on Docusaurus Documentation
// This file contains all mock data for the DataHub platform

// User and Authentication Data
export const userData = {
    currentUser: {
        id: 'user_001',
        email: 'user@example.com',
        name: 'Data Scientist',
        role: 'data_scientist',
        organization: 'DataHub Corp',
        avatar: '/assets/images/avatar.png',
        permissions: ['read', 'write', 'execute', 'admin'],
        lastLogin: '2024-01-15T10:30:00Z',
        isActive: true
    },
    organizations: [
        {
            id: 'org_001',
            name: 'DataHub Corp',
            domain: 'datahub.io',
            plan: 'enterprise',
            maxUsers: 100,
            maxStorage: '1TB',
            features: ['jupyterhub', 'airflow', 'spark', 'mlflow']
        }
    ]
};

// Projects Data
export const projectsData = {
    projects: [
        {
            id: 'proj_001',
            name: 'Customer Analytics Project',
            description: 'Advanced customer behavior analysis using machine learning',
            status: 'active',
            createdAt: '2024-01-01T00:00:00Z',
            updatedAt: '2024-01-15T10:30:00Z',
            owner: 'user_001',
            tags: ['analytics', 'ml', 'customer'],
            storage: {
                used: '2.5GB',
                limit: '10GB',
                percentage: 25
            },
            services: {
                jupyterhub: { status: 'running', url: 'http://localhost:8001' },
                airflow: { status: 'running', url: 'http://localhost:8080' },
                mongodb: { status: 'connected', database: 'proj_001_db' },
                spark: { status: 'ready', cluster: 'spark-cluster-001' }
            },
            members: [
                { id: 'user_001', role: 'owner', permissions: ['read', 'write', 'execute', 'admin'] },
                { id: 'user_002', role: 'member', permissions: ['read', 'write'] },
                { id: 'user_003', role: 'viewer', permissions: ['read'] }
            ]
        },
        {
            id: 'proj_002',
            name: 'Sales Forecasting ML',
            description: 'Predictive analytics for sales forecasting',
            status: 'active',
            createdAt: '2024-01-05T00:00:00Z',
            updatedAt: '2024-01-14T15:20:00Z',
            owner: 'user_001',
            tags: ['forecasting', 'sales', 'ml'],
            storage: {
                used: '1.8GB',
                limit: '10GB',
                percentage: 18
            },
            services: {
                jupyterhub: { status: 'stopped', url: 'http://localhost:8001' },
                airflow: { status: 'running', url: 'http://localhost:8080' },
                mongodb: { status: 'connected', database: 'proj_002_db' },
                spark: { status: 'ready', cluster: 'spark-cluster-002' }
            },
            members: [{ id: 'user_001', role: 'owner', permissions: ['read', 'write', 'execute', 'admin'] }]
        },
        {
            id: 'proj_003',
            name: 'Data Pipeline ETL',
            description: 'Extract, Transform, Load pipeline for data processing',
            status: 'archived',
            createdAt: '2023-12-01T00:00:00Z',
            updatedAt: '2024-01-10T09:15:00Z',
            owner: 'user_001',
            tags: ['etl', 'pipeline', 'data-processing'],
            storage: {
                used: '5.2GB',
                limit: '10GB',
                percentage: 52
            },
            services: {
                jupyterhub: { status: 'stopped', url: 'http://localhost:8001' },
                airflow: { status: 'stopped', url: 'http://localhost:8080' },
                mongodb: { status: 'disconnected', database: 'proj_003_db' },
                spark: { status: 'stopped', cluster: 'spark-cluster-003' }
            },
            members: [{ id: 'user_001', role: 'owner', permissions: ['read', 'write', 'execute', 'admin'] }]
        }
    ],
    totalProjects: 3,
    activeProjects: 2,
    archivedProjects: 1
};

// Storage Data
export const storageData = {
    overview: {
        totalUsed: '9.5GB',
        totalLimit: '30GB',
        percentage: 31.7,
        files: 1250,
        folders: 45
    },
    byProject: [
        { projectId: 'proj_001', name: 'Customer Analytics', used: '2.5GB', files: 450 },
        { projectId: 'proj_002', name: 'Sales Forecasting', used: '1.8GB', files: 320 },
        { projectId: 'proj_003', name: 'Data Pipeline ETL', used: '5.2GB', files: 480 }
    ],
    recentFiles: [
        {
            id: 'file_001',
            name: 'customer_data_2024.csv',
            size: '250MB',
            type: 'csv',
            project: 'proj_001',
            uploadedAt: '2024-01-15T10:25:00Z',
            path: '/data/raw/customer_data_2024.csv'
        },
        {
            id: 'file_002',
            name: 'sales_forecast_model.pkl',
            size: '45MB',
            type: 'pickle',
            project: 'proj_002',
            uploadedAt: '2024-01-14T16:30:00Z',
            path: '/models/sales_forecast_model.pkl'
        },
        {
            id: 'file_003',
            name: 'data_pipeline_dag.py',
            size: '2.1MB',
            type: 'python',
            project: 'proj_003',
            uploadedAt: '2024-01-13T14:20:00Z',
            path: '/workflows/data_pipeline_dag.py'
        }
    ],
    storageBackends: [
        { name: 'MinIO', status: 'active', used: '6.2GB', total: '20GB' },
        { name: 'S3', status: 'active', used: '3.3GB', total: '10GB' }
    ]
};

// JupyterHub Data
export const jupyterHubData = {
    status: 'running',
    url: 'http://localhost:8001',
    servers: [
        {
            id: 'server_001',
            projectId: 'proj_001',
            projectName: 'Customer Analytics Project',
            status: 'running',
            startedAt: '2024-01-15T09:00:00Z',
            resources: {
                cpu: '2 cores',
                memory: '4GB',
                gpu: 'None'
            },
            notebooks: [
                { name: 'data_analysis.ipynb', lastModified: '2024-01-15T10:30:00Z' },
                { name: 'customer_segmentation.ipynb', lastModified: '2024-01-14T16:45:00Z' }
            ]
        },
        {
            id: 'server_002',
            projectId: 'proj_002',
            projectName: 'Sales Forecasting ML',
            status: 'stopped',
            startedAt: null,
            resources: {
                cpu: '4 cores',
                memory: '8GB',
                gpu: '1x Tesla V100'
            },
            notebooks: [
                { name: 'sales_forecast.ipynb', lastModified: '2024-01-14T15:20:00Z' },
                { name: 'model_evaluation.ipynb', lastModified: '2024-01-13T11:10:00Z' }
            ]
        }
    ],
    availableResources: {
        totalCpu: '16 cores',
        totalMemory: '32GB',
        totalGpu: '2x Tesla V100',
        availableCpu: '10 cores',
        availableMemory: '20GB',
        availableGpu: '1x Tesla V100'
    }
};

// Airflow Data
export const airflowData = {
    status: 'running',
    url: 'http://localhost:8080',
    dags: [
        {
            id: 'dag_001',
            name: 'customer_data_pipeline',
            projectId: 'proj_001',
            status: 'active',
            schedule: '0 2 * * *', // Daily at 2 AM
            lastRun: '2024-01-15T02:00:00Z',
            nextRun: '2024-01-16T02:00:00Z',
            tasks: [
                { name: 'extract_customer_data', status: 'success', duration: '5m 30s' },
                { name: 'transform_data', status: 'success', duration: '12m 15s' },
                { name: 'load_to_warehouse', status: 'success', duration: '8m 45s' }
            ]
        },
        {
            id: 'dag_002',
            name: 'sales_forecast_daily',
            projectId: 'proj_002',
            status: 'active',
            schedule: '0 6 * * *', // Daily at 6 AM
            lastRun: '2024-01-15T06:00:00Z',
            nextRun: '2024-01-16T06:00:00Z',
            tasks: [
                { name: 'load_sales_data', status: 'success', duration: '3m 20s' },
                { name: 'preprocess_features', status: 'success', duration: '7m 10s' },
                { name: 'run_forecast', status: 'success', duration: '15m 30s' },
                { name: 'save_predictions', status: 'success', duration: '2m 45s' }
            ]
        }
    ],
    recentRuns: [
        {
            dagId: 'dag_001',
            runId: 'run_001',
            startDate: '2024-01-15T02:00:00Z',
            endDate: '2024-01-15T02:26:30Z',
            status: 'success',
            duration: '26m 30s'
        },
        {
            dagId: 'dag_002',
            runId: 'run_002',
            startDate: '2024-01-15T06:00:00Z',
            endDate: '2024-01-15T06:28:45Z',
            status: 'success',
            duration: '28m 45s'
        }
    ]
};

// MongoDB Data
export const mongodbData = {
    status: 'connected',
    databases: [
        {
            name: 'proj_001_db',
            projectId: 'proj_001',
            collections: 12,
            documents: 15420,
            size: '1.2GB',
            indexes: 8
        },
        {
            name: 'proj_002_db',
            projectId: 'proj_002',
            collections: 8,
            documents: 8920,
            size: '650MB',
            indexes: 5
        }
    ],
    recentQueries: [
        {
            database: 'proj_001_db',
            collection: 'customers',
            operation: 'find',
            duration: '45ms',
            timestamp: '2024-01-15T10:30:00Z'
        },
        {
            database: 'proj_002_db',
            collection: 'sales',
            operation: 'aggregate',
            duration: '120ms',
            timestamp: '2024-01-15T10:25:00Z'
        }
    ]
};

// API Gateway Data
export const apiGatewayData = {
    status: 'active',
    endpoints: [
        {
            path: '/api/v1/projects',
            method: 'GET',
            requests: 1250,
            avgResponseTime: '45ms',
            successRate: 99.8
        },
        {
            path: '/api/v1/storage/upload',
            method: 'POST',
            requests: 89,
            avgResponseTime: '2.3s',
            successRate: 98.5
        },
        {
            path: '/api/v1/jupyterhub/start',
            method: 'POST',
            requests: 12,
            avgResponseTime: '15.2s',
            successRate: 100
        }
    ],
    rateLimits: {
        requestsPerMinute: 1000,
        requestsPerHour: 50000,
        currentUsage: 45
    }
};

// Dashboard Metrics
export const dashboardMetrics = {
    systemHealth: {
        overall: 'healthy',
        services: [
            { name: 'Frontend', status: 'healthy', uptime: '99.95%' },
            { name: 'Backend API', status: 'healthy', uptime: '99.92%' },
            { name: 'JupyterHub', status: 'healthy', uptime: '99.88%' },
            { name: 'Airflow', status: 'healthy', uptime: '99.90%' },
            { name: 'MongoDB', status: 'healthy', uptime: '99.98%' },
            { name: 'Redis', status: 'healthy', uptime: '99.99%' }
        ]
    },
    usageStats: {
        activeUsers: 15,
        totalProjects: 3,
        totalStorage: '9.5GB',
        totalNotebooks: 8,
        activeWorkflows: 2
    },
    recentActivity: [
        {
            type: 'file_upload',
            user: 'user@example.com',
            project: 'Customer Analytics Project',
            description: 'Uploaded customer_data_2024.csv',
            timestamp: '2024-01-15T10:25:00Z'
        },
        {
            type: 'notebook_created',
            user: 'user@example.com',
            project: 'Sales Forecasting ML',
            description: 'Created sales_forecast.ipynb',
            timestamp: '2024-01-15T09:45:00Z'
        },
        {
            type: 'workflow_completed',
            user: 'system',
            project: 'Customer Analytics Project',
            description: 'Completed customer_data_pipeline DAG',
            timestamp: '2024-01-15T02:26:30Z'
        }
    ]
};

// User Management Data
export const userManagementData = {
    users: [
        {
            id: 'user_001',
            email: 'user@example.com',
            name: 'Data Scientist',
            role: 'data_scientist',
            status: 'active',
            lastLogin: '2024-01-15T10:30:00Z',
            projects: ['proj_001', 'proj_002', 'proj_003']
        },
        {
            id: 'user_002',
            email: 'analyst@example.com',
            name: 'Data Analyst',
            role: 'data_analyst',
            status: 'active',
            lastLogin: '2024-01-14T16:20:00Z',
            projects: ['proj_001']
        },
        {
            id: 'user_003',
            email: 'viewer@example.com',
            name: 'Business Viewer',
            role: 'viewer',
            status: 'active',
            lastLogin: '2024-01-13T11:45:00Z',
            projects: ['proj_001']
        }
    ],
    groups: [
        {
            id: 'group_001',
            name: 'Data Science Team',
            description: 'Core data science team members',
            members: ['user_001', 'user_002'],
            permissions: ['read', 'write', 'execute']
        },
        {
            id: 'group_002',
            name: 'Business Stakeholders',
            description: 'Business users with read-only access',
            members: ['user_003'],
            permissions: ['read']
        }
    ],
    roles: [
        {
            name: 'admin',
            description: 'Full system access',
            permissions: ['read', 'write', 'execute', 'admin']
        },
        {
            name: 'data_scientist',
            description: 'Data science and ML capabilities',
            permissions: ['read', 'write', 'execute']
        },
        {
            name: 'data_analyst',
            description: 'Data analysis and reporting',
            permissions: ['read', 'write']
        },
        {
            name: 'viewer',
            description: 'Read-only access',
            permissions: ['read']
        }
    ]
};

export default {
    userData,
    projectsData,
    storageData,
    jupyterHubData,
    airflowData,
    mongodbData,
    apiGatewayData,
    dashboardMetrics,
    userManagementData
};
